<div class="benchmark-dialog bg-light-surface dark:bg-dark-surface flex flex-col max-h-[92vh]">
  <div class="px-5 py-4 border-b border-light-border dark:border-dark-border flex items-start justify-between gap-4">
    <div class="min-w-0">
      <div class="flex items-center gap-2">
        <lucide-angular [img]="BenchmarkIcon" class="w-5 h-5 text-light-primary dark:text-dark-primary"></lucide-angular>
        <h1 class="!mb-0">Benchmark de Conversación</h1>
      </div>
      <p class="normal !mb-0 mt-1">Resumen por turno con tiempos de respuesta y secuencia completa de trazas.</p>
      <p class="small !mb-0 mt-1">Session ID: {{ data.sessionId }}</p>
      <p class="small !mb-0 mt-1">ConversationID: {{ getConversationIdForDisplay() }}</p>
      <p class="small !mb-0 mt-1">Fecha: {{ getConversationDateForDisplay() }}</p>
    </div>

    <div class="flex items-center gap-2">
      <button
        type="button"
        (click)="copyBenchmarkToClipboard()"
        [disabled]="benchmarkRows().length === 0"
        class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-light-border dark:border-dark-border text-light-text dark:text-dark-text hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        matTooltip="Copiar métricas y tabla para pegar en Sheets"
      >
        <lucide-angular [img]="CopyIcon" class="w-4 h-4"></lucide-angular>
        <span class="text-sm font-medium">Copiar todo</span>
      </button>

      <button
        type="button"
        (click)="copyBenchmarkValuesToClipboard()"
        [disabled]="benchmarkRows().length === 0"
        class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-light-border dark:border-dark-border text-light-text dark:text-dark-text hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        matTooltip="Copiar solo valores (sin etiquetas) para pegar en Sheets"
      >
        <lucide-angular [img]="CopyIcon" class="w-4 h-4"></lucide-angular>
        <span class="text-sm font-medium">Copiar solo info</span>
      </button>

      <button
        mat-icon-button
        type="button"
        (click)="close()"
        class="rounded-xl border border-light-border dark:border-dark-border hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
        matTooltip="Cerrar"
      >
        <lucide-angular [img]="CloseIcon" class="w-5 h-5 text-light-text dark:text-dark-text"></lucide-angular>
      </button>
    </div>
  </div>

  <div class="p-5 overflow-auto dark:[color-scheme:dark] flex-1">
    @if (benchmarkRows().length > 0) {
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div class="rounded-lg border border-light-border dark:border-dark-border bg-light-bg-secondary dark:bg-dark-bg-secondary px-3 py-2">
          <p class="small !mb-0">Total (s)</p>
          <p class="text-sm font-semibold text-light-text dark:text-dark-text !mb-0">{{ formatSeconds(totalBenchmarkSeconds()) }}</p>
        </div>
        <div class="rounded-lg border border-light-border dark:border-dark-border bg-light-bg-secondary dark:bg-dark-bg-secondary px-3 py-2">
          <p class="small !mb-0">Promedio (s)</p>
          <p class="text-sm font-semibold text-light-text dark:text-dark-text !mb-0">{{ formatSeconds(averageBenchmarkSeconds()) }}</p>
        </div>
        <div class="rounded-lg border border-light-border dark:border-dark-border bg-light-bg-secondary dark:bg-dark-bg-secondary px-3 py-2">
          <p class="small !mb-0">Mínimo (s)</p>
          <p class="text-sm font-semibold text-light-text dark:text-dark-text !mb-0">{{ formatSeconds(minBenchmarkSeconds()) }}</p>
        </div>
        <div class="rounded-lg border border-light-border dark:border-dark-border bg-light-bg-secondary dark:bg-dark-bg-secondary px-3 py-2">
          <p class="small !mb-0">Máximo (s)</p>
          <p class="text-sm font-semibold text-light-text dark:text-dark-text !mb-0">{{ formatSeconds(maxBenchmarkSeconds()) }}</p>
        </div>
      </div>

      <div class="rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
        <div class="overflow-auto max-h-[70vh] scrollbar-thin">
          <table class="w-full min-w-[1000px] text-sm" aria-label="Tabla de benchmark por conversación">
            <caption class="sr-only">Tabla resumen por turnos con tiempo de respuesta y trazas del agente</caption>
            <thead>
              <tr class="border-b border-light-border dark:border-dark-border">
                <th class="px-3 py-3 text-left font-semibold text-light-text dark:text-dark-text sticky top-0 bg-light-surface dark:bg-dark-surface">Turno</th>
                <th class="px-3 py-3 text-left font-semibold text-light-text dark:text-dark-text sticky top-0 bg-light-surface dark:bg-dark-surface">Mensaje</th>
                <th class="px-3 py-3 text-right font-semibold text-light-text dark:text-dark-text sticky top-0 bg-light-surface dark:bg-dark-surface">Tiempo (s)</th>
                <th class="px-3 py-3 text-left font-semibold text-light-text dark:text-dark-text sticky top-0 bg-light-surface dark:bg-dark-surface">Traza</th>
              </tr>
            </thead>
            <tbody>
              @for (row of benchmarkRows(); track row.turn) {
                <tr class="border-b border-light-border/70 dark:border-dark-border/70 align-top">
                  <td class="px-3 py-3 font-medium text-light-text dark:text-dark-text">{{ row.turn }}</td>
                  <td class="px-3 py-3">
                    <p class="text-xs text-light-text-secondary dark:text-dark-text-secondary m-0" [title]="row.fullUserMessage">
                      <span class="font-semibold text-light-primary dark:text-dark-primary">user:</span>
                      {{ row.userMessage }}
                    </p>
                    <p class="text-xs text-light-text-secondary dark:text-dark-text-secondary m-0 mt-2" [title]="row.fullBotMessage">
                      <span class="font-semibold text-light-text dark:text-dark-text">bot:</span>
                      {{ row.botMessage }}
                    </p>
                  </td>
                  <td class="px-3 py-3 text-right font-semibold text-light-text dark:text-dark-text">{{ formatSeconds(row.responseTimeSeconds) }}</td>
                  <td class="px-3 py-3 whitespace-normal break-words">
                    @if (row.traceSteps.length > 0) {
                      <div class="flex flex-wrap items-center gap-1" [title]="row.traceSummary">
                        @for (step of row.traceSteps; track $index) {
                          <span [class]="step.className">{{ step.label }}</span>
                          @if (!$last) {
                            <span class="text-light-text-tertiary dark:text-dark-text-tertiary text-xs font-medium">→</span>
                          }
                        }
                      </div>
                    } @else {
                      <span class="text-sm text-light-text-secondary dark:text-dark-text-secondary">-</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    } @else {
      <div class="rounded-xl border border-light-border dark:border-dark-border bg-light-bg-secondary dark:bg-dark-bg-secondary px-4 py-3">
        <p class="normal !mb-0">No hay suficientes mensajes para calcular el benchmark de esta conversación.</p>
      </div>
    }
  </div>
</div>
